/**
 * Layout Principal da Aplicação
 *
 * Layout estilo ERP com sidebar lateral e header fixo.
 * Usado em todas as páginas protegidas da aplicação.
 *
 * Estrutura:
 * - Sidebar: Menu de navegação lateral (responsivo)
 * - Header: Barra superior com notificações e perfil
 * - Main: Conteúdo principal (Outlet)
 *
 * Comportamento responsivo:
 * - Desktop (lg+): Sidebar fixa, pode colapsar
 * - Mobile (<lg): Sidebar em overlay com backdrop
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { Sidebar } from '@components/layout/Sidebar';
import { Header } from '@components/layout/Header';
import { SubscriptionBanner } from '@components/layout/SubscriptionBanner';
import { useSidebar } from '@contexts/SidebarContext';
import { useNotificationListener } from '@hooks/useNotificationListener';
import { useDeliveryDecisionAlert } from '@hooks/useDeliveryDecisionAlert';
import { useAuth } from '@hooks/useAuth';
import { NoticePopup } from '@components/shared/NoticePopup';
import { GlobalNewOrderAlert } from '@components/layout/GlobalNewOrderAlert';
import { cn } from '@utils/helpers';
import { Spinner } from '@components/ui/Spinner';

/**
 * Componente MainLayout
 *
 * Layout principal que envolve todas as páginas protegidas.
 * Usa SidebarContext para gerenciar estado global da sidebar.
 */
export function MainLayout() {
  // Usa contexto global da sidebar
  const {
    isCollapsed,
    isMobileOpen,
    toggleCollapsed,
    closeMobile,
    toggleMobile,
  } = useSidebar();

  // Localização atual para fechar menu ao navegar
  const location = useLocation();

  // Escuta notificações em tempo real via WebSocket
  useNotificationListener();

  // Entrega por aplicativo (#231): alerta com som quando a loja precisa decidir
  const { user } = useAuth();
  useDeliveryDecisionAlert(['SUPER_ADMIN', 'OWNER', 'MANAGER', 'ATTENDANT'].includes(user?.role ?? ''));

  // Fecha menu mobile ao mudar de rota
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  /**
   * Toggle do menu (diferencia desktop e mobile)
   */
  const handleMenuToggle = () => {
    // Em telas menores que lg (1024px), abre o menu mobile
    if (window.innerWidth < 1024) {
      toggleMobile();
    } else {
      // Em desktop, colapsa a sidebar
      toggleCollapsed();
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-900">
      {/* Overlay do menu mobile (backdrop) */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Mobile (overlay) */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-[280px] transform transition-transform duration-300 ease-in-out lg:hidden',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Botão de fechar no mobile */}
        <button
          onClick={closeMobile}
          className="absolute right-4 top-4 z-10 rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
          aria-label="Fechar menu"
        >
          <X size={24} />
        </button>

        <Sidebar
          collapsed={false}
          onToggle={closeMobile}
          onLeave={closeMobile}
        />
      </div>

      {/* Sidebar Desktop (fixa) */}
      <div className="hidden lg:block">
        <Sidebar
          collapsed={isCollapsed}
          onToggle={toggleCollapsed}
        />
      </div>

      {/* Container principal */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header superior */}
        <Header onMenuClick={handleMenuToggle} />

        {/* Aviso de período de teste / pagamento pendente */}
        <SubscriptionBanner />

        {/* Área de conteúdo principal */}
        <main className="flex-1 overflow-auto p-4 md:p-6">
          {/* As páginas são carregadas sob demanda: o menu continua visível */}
          <Suspense
            fallback={
              <div className="flex h-64 items-center justify-center">
                <Spinner size="lg" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Popup de avisos internos (RH) */}
      <NoticePopup />

      {/* Alerta global de novos pedidos (funciona em qualquer página) */}
      <GlobalNewOrderAlert />
    </div>
  );
}
